import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Play, Clock, Sparkles, Lock, Filter, Unlock, Share2 } from 'lucide-react';
import ShareButton from '../components/ShareButton';

export default function Meditations() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [meditations, setMeditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState('all'); // 'all', 'free', 'premium'

  useEffect(() => {
    loadMeditations();
  }, []);

  const loadMeditations = async () => {
    try {
      const response = await axios.get(`${API}/meditations`);
      setMeditations(response.data);
    } catch (error) {
      console.error('Failed to load meditations:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(meditations.map(m => m.category))];
  
  let filteredMeditations = filter === 'all' 
    ? meditations 
    : meditations.filter(m => m.category === filter);
  
  // Apply access filter
  if (accessFilter === 'free') {
    filteredMeditations = filteredMeditations.filter(m => !m.is_premium);
  } else if (accessFilter === 'premium') {
    filteredMeditations = filteredMeditations.filter(m => m.is_premium);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 rounded-full animate-spin"
             style={{ borderColor: `${currentColors?.primary}30`, borderTopColor: currentColors?.primary }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="meditations-page">
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
            Guided Meditations
          </h1>
          <p className="font-cormorant" style={{ color: currentColors.textMuted }}>
            Embark on a journey of inner peace and cosmic connection
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Category filter */}
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <Filter className="w-5 h-5" style={{ color: currentColors.primary }} />
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className="px-4 py-2 rounded-lg text-sm font-cormorant transition-all"
              style={{
                backgroundColor: filter === cat ? `${currentColors.primary}20` : `${currentColors.background}80`,
                color: filter === cat ? currentColors.primary : currentColors.textMuted,
                border: `1px solid ${filter === cat ? `${currentColors.primary}40` : `${currentColors.primary}10`}`
              }}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Access type filter */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAccessFilter('all')}
            className="px-3 py-2 rounded-lg text-xs transition-all"
            style={{
              backgroundColor: accessFilter === 'all' ? `${currentColors.secondary}20` : 'transparent',
              color: accessFilter === 'all' ? currentColors.secondary : currentColors.textMuted,
              border: `1px solid ${accessFilter === 'all' ? `${currentColors.secondary}40` : `${currentColors.border}`}`
            }}
          >
            All
          </button>
          <button
            onClick={() => setAccessFilter('free')}
            className="px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-1"
            style={{
              backgroundColor: accessFilter === 'free' ? 'rgba(34, 197, 94, 0.2)' : 'transparent',
              color: accessFilter === 'free' ? '#22c55e' : currentColors.textMuted,
              border: `1px solid ${accessFilter === 'free' ? 'rgba(34, 197, 94, 0.4)' : currentColors.border}`
            }}
          >
            <Unlock className="w-3 h-3" />
            Free
          </button>
          <button
            onClick={() => setAccessFilter('premium')}
            className="px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-1"
            style={{
              backgroundColor: accessFilter === 'premium' ? `${currentColors.accent}20` : 'transparent',
              color: accessFilter === 'premium' ? currentColors.accent : currentColors.textMuted,
              border: `1px solid ${accessFilter === 'premium' ? `${currentColors.accent}40` : currentColors.border}`
            }}
          >
            <Lock className="w-3 h-3" />
            Premium
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMeditations.map((meditation) => (
          <Link
            key={meditation.id}
            to={`/meditations/${meditation.id}`}
            className="glass-card group overflow-hidden hover:scale-[1.02] transition-all duration-300"
            data-testid={`meditation-card-${meditation.id}`}
          >
            <div className="relative h-48 overflow-hidden">
              {meditation.image_url ? (
                <img 
                  src={meditation.image_url} 
                  alt={meditation.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              ) : (
                <div 
                  className="w-full h-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${currentColors.primary}30, ${currentColors.secondary}30)` }}
                >
                  <Sparkles className="w-12 h-12" style={{ color: `${currentColors.primary}50` }} />
                </div>
              )}
              <div 
                className="absolute inset-0"
                style={{ background: `linear-gradient(to top, ${currentColors.background}e6, transparent)` }}
              ></div>
              
              {/* Access Badge */}
              <div className="absolute top-3 right-3">
                {meditation.is_premium ? (
                  <div 
                    className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                    style={{ backgroundColor: currentColors.accent, color: currentColors.background }}
                  >
                    <Lock className="w-3 h-3" />
                    Premium
                  </div>
                ) : (
                  <div 
                    className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                    style={{ backgroundColor: 'rgba(34, 197, 94, 0.9)', color: '#fff' }}
                  >
                    <Unlock className="w-3 h-3" />
                    Free
                  </div>
                )}
              </div>
              
              <div 
                className="absolute bottom-3 right-3 w-12 h-12 backdrop-blur rounded-full flex items-center justify-center transition-all"
                style={{ backgroundColor: `${currentColors.primary}30` }}
              >
                <Play className="w-5 h-5 ml-0.5" style={{ color: currentColors.primary }} />
              </div>
            </div>
            
            <div className="p-5">
              <h3 
                className="font-cinzel text-lg mb-2 transition-colors"
                style={{ color: currentColors.text }}
              >
                {meditation.title}
              </h3>
              <p 
                className="text-sm font-cormorant line-clamp-2 mb-4"
                style={{ color: currentColors.textMuted }}
              >
                {meditation.description}
              </p>
              <div className="flex items-center justify-between">
                <span 
                  className="text-xs px-2 py-1 rounded"
                  style={{ backgroundColor: `${currentColors.secondary}15`, color: currentColors.secondary }}
                >
                  {meditation.category}
                </span>
                <div className="flex items-center gap-2">
                  <span 
                    className="flex items-center gap-1 text-sm"
                    style={{ color: currentColors.textMuted }}
                  >
                    <Clock className="w-4 h-4" />
                    {meditation.duration} min
                  </span>
                  <div onClick={(e) => e.preventDefault()}>
                    <ShareButton
                      title={meditation.title}
                      description={meditation.description}
                      imageUrl={meditation.image_url}
                      contentType="meditation"
                      contentId={meditation.id}
                      variant="icon"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filteredMeditations.length === 0 && (
        <div className="text-center py-16">
          <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: `${currentColors.primary}30` }} />
          <p className="font-cormorant text-lg" style={{ color: currentColors.textMuted }}>
            No meditations found with the selected filters
          </p>
        </div>
      )}
    </div>
  );
}
